import { NumberFormComponent } from "components/Form/components/Number/NumberFormComponent";
import { Field } from "react-final-form";

/**
 * Container setting for `max_tracked_record_bytes` on PROJECT / BUNDLE / WORKSPACE:
 * the git-track size guard refuses to git-track a record whose serialized export
 * exceeds this limit. Empty = no limit. Sits next to "Default track in git" in
 * the details sidebar.
 *
 * The value is entered and displayed in kB (1 kB = 1024 bytes), with a "kB" unit
 * suffix sitting inline immediately to the right of the input, so the user works
 * in a human-scaled unit instead of a long raw-byte number. The backend column
 * stays in bytes; the kB<->bytes conversion happens at load (initialValue) and
 * submit in ResourceDetails.
 * (operator-task-fe-max-tracked-record-bytes-container-setting)
 */
export const MaxTrackedRecordBytesField = ({
    label,
    handleSubmit,
    isDisabled,
    initialValue,
}: {
    label?: string;
    handleSubmit?: () => any;
    isDisabled?: boolean;
    initialValue?: any;
}) => (
    <Field name="max_tracked_record_bytes" initialValue={initialValue}>
        {({ input, meta }) => (
            <NumberFormComponent
                input={{
                    ...input,
                    // Sanitize to digits on change. This restores paste /
                    // select-all-replace, which an earlier key-level onKeyDown
                    // filter swallowed (Ctrl+V fires a "v" keydown that was
                    // preventDefault()'d).
                    onChange: (event: any) => {
                        const raw = event && event.target != null ? event.target.value : event;
                        const digits = raw == null ? "" : String(raw).replace(/[^0-9]/g, "");
                        input.onChange(digits);
                    },
                    onBlur: () => {
                        if (!meta.pristine) {
                            handleSubmit?.();
                        }
                    },
                }}
                fieldProps={{
                    label,
                    // "kB" unit rendered inline right after the input (a
                    // `.form__actionButtons` flex child) rather than as an
                    // absolutely-positioned right adornment: the adornment pins to
                    // the full-width field container, so with a compact number
                    // input it stranded the unit far to the right of the value.
                    // The palette-token `input__icon-text` keeps the silver-grey
                    // unit styling (ui-design-principle-color-palette).
                    actionButtons: <span className="input__icon-text">kB</span>,
                    docs: {
                        urlPath: "/docs/git-integration#limit-the-size-of-tracked-records",
                    },
                }}
                fieldAttr={{ min: "0", disabled: isDisabled }}
                meta={meta}
            />
        )}
    </Field>
);
